import { createTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    primary: {
      main: "#031B89",
      light: "#07902E",
    },
    _orange: {
      main: "#fa4706",
    },
    secondary: {
      main: "#07902E",
    },
    _blue: {
      main: "#1976d2",
      light: "#d9edfa",
    },
  },
  customColors: {
    text: {
      primary: "#374151",
      secondary: "#6B7280",
      disabled: "#9CA3AF",
      muted: "#757575",
      strong: "#374151",
      iconMuted: "#64748B",
    },
    background: {
      main: "#FFFFFF",
      tableRow: "#F3F4F6",
      hover: "#FAFAFA",
      cardHeader: "#F7F7FC",
      dialog: "#F1F5F9",
      info: "#F0F7FF",
      header: "#F9FAFB",
      lightGrayBg: "#eeeeee",
      infoBox: "#FAFBFC",
      light: "#F3F4F6",
    },
    border: {
      main: "#E8EAED",
      subtle: "#E5E7EB",
      strong: "#E0E0E0",
      disabled: "#CBD5E1",
      accent: "#E2E8F0",
      dashed: "#C5C9CF",
    },
    button: {
      primary: "#1976d2",
      dark: "#031B89",
      hover: "#2137a6",
      text: "#FFFFFF",
      success: {
        main: "#10B981",
        dark: "#059669",
        light: "#D1FAE5",
      },
    },
    status: {
      success: {
        main: "#10B981",
        text: "#057F28",
        bg: "#E8FBF3",
      },
      warning: {
        main: "#F59E0B",
        text: "#D97706",
        bg: "#FFF7E0",
        alert: "#FF8800",
      },
      error: {
        main: "#EF4444",
        text: "#C71414",
        bg: "#FEEBEB",
        border: "#ef4444",
      },
      info: {
        main: "#3B82F6",
        text: "#1976d2",
        bg: "#e3f2fd",
      },
      pending: {
        main: "#FF9800",
        bg: "#FFF4E5",
        text: "#FF9800",
      },
      completed: {
        bg: "#E8FBF3",
      },
    },
    shadow: {
      main: "0 2px 8px #00000010",
      card: "0 1px 3px rgba(0,0,0,0.1)",
      cardHover: "0 2px 12px rgba(0, 0, 0, 0.1)",
      cardSelected: "0 2px 8px rgba(0, 0, 0, 0.08)",
      filterChip: "0 4px 12px rgba(59, 130, 246, 0.2)",
    },
    icons: {
      primary: "#3b82f6",
      secondary: "#6B7280",
      disabled: "#9CA3AF",
      warning: "#F59E0B",
      success: "#10B981",
      edit: "#6B7280",
      tooltip: "#6B7280",
      dark: "#031B89",
    },
    gradients: {
      green: {
        start: "#4ade80",
      },
    },
    // light scrollbar 
    scrollbar: {
      width: "6px",
      track: "transparent",
      thumb: {
        default: 0.5,
        hover: 0.7,
      },
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "unset",
          minWidth: "unset",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "black !important",
          color: "white",
        },
        arrow: {
          color: "black",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "unset",
        },
      },
    },
    // Box variants
    MuiBox: {
      variants: [
        {
          // Info box container (for descriptions, info sections)
          props: { variant: "infoBox" },
          style: ({ theme }) => ({
            backgroundColor: theme.customColors.background.infoBox,
            borderRadius: "8px",
            border: `1px solid ${alpha(theme.customColors.border.main, 0.2)}`,
          }),
        },
        {
          // Empty state container (centered layout)
          props: { variant: "emptyState" },
          style: {
            textAlign: "center",
            py: 6,
            px: 4,
          },
        },
        {
          // Scrollable container with custom scrollbar
          props: { variant: "scrollable" },
          style: ({ theme }) => ({
            "&::-webkit-scrollbar": {
              width: theme.customColors.scrollbar.width,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.customColors.scrollbar.track,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(
                theme.customColors.border.main,
                theme.customColors.scrollbar.thumb.default
              ),
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: alpha(
                  theme.customColors.border.main,
                  theme.customColors.scrollbar.thumb.hover
                ),
              },
            },
          }),
        },
      ],
    },
  },
  typography: {
    fontFamily: "Montserrat, sans-serif",
    h1: {
      fontSize: "32px",
      fontWeight: 700,
      color: "#374151",
      lineHeight: 1.25,
      letterSpacing: "normal",
      fontFamily: "Montserrat, sans-serif",
    },
    h2: {
      fontSize: "28px",
      fontWeight: 700,
      color: "#374151",
      lineHeight: 1.25,
      letterSpacing: "normal",
      fontFamily: "Montserrat, sans-serif",
    },
    h3: {
      fontSize: "24px",
      fontWeight: 700,
      color: "#374151",
      lineHeight: 1.3,
      letterSpacing: "normal",
      fontFamily: "Montserrat, sans-serif",
    },
    h4: {
      fontSize: "20px",
      fontWeight: 700,
      color: "#374151",
      lineHeight: 1.3,
      fontFamily: "Montserrat, sans-serif",
    },
    h5: {
      // Page / section title
      fontSize: "18px",
      fontWeight: 600,
      color: "#374151",
      lineHeight: 1.4,
      letterSpacing: "normal",
      fontFamily: "Montserrat, sans-serif",
    },
    h6: {
      fontSize: "16px",
      fontWeight: 600,
      color: "#374151",
      lineHeight: 1.4,
      fontFamily: "Montserrat, sans-serif",
    },
    subtitle1: {
      fontFamily: "Montserrat, sans-serif",
    },
    subtitle2: {
      // Secondary / empty-state subtitle
      fontSize: "13px",
      color: "#6B7280",
      fontFamily: "Montserrat, sans-serif",
    },
    body1: {
      fontSize: "15px",
      color: "#374151",
      lineHeight: 1.6,
      fontWeight: 400,
      letterSpacing: "normal",
      fontFamily: "Montserrat, sans-serif",
    },
    body2: {
      fontSize: "14px",
      color: "#6B7280",
      fontFamily: "Montserrat, sans-serif",
    },
    caption: {
      fontSize: "12px",
      fontWeight: 500,
      color: "#6B7280",
      letterSpacing: "0.5px",
      fontFamily: "Montserrat, sans-serif",
    },
    overline: {
      fontSize: "13px",
      fontWeight: 600,
      color: "#374151",
      letterSpacing: "0.5px",
      fontFamily: "Montserrat, sans-serif",
    },
  },
});

export default theme;
